import React, { useEffect, useMemo, useState } from "react";
import { db } from "../../lib/firebase";
import {
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";

function safe(v) {
  return String(v ?? "").trim();
}

function toTime(ts) {
  try {
    if (!ts) return "";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString();
  } catch {
    return "";
  }
}

export default function AdminUsersScreen({ goBack, role }) {
  const roleNorm = safe(role).toUpperCase(); // handles "admin" vs "ADMIN"
  const isAdmin = roleNorm === "ADMIN";

  const [qText, setQText] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    setLoading(true);
    setMsg("");

    const ref = collection(db, "users");

    // Try orderBy(createdAt). If some docs don't have createdAt, it can fail.
    const q = query(ref, orderBy("createdAt", "desc"), limit(200));

    const unsub = onSnapshot(
      q,
      (snap) => {
        const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setRows(data);
        setLoading(false);

        // keep selection in sync if user doc updates
        if (selected?.id) {
          const latest = data.find((x) => x.id === selected.id);
          if (latest) setSelected(latest);
        }
      },
      (err) => {
        console.log("Users listener error:", err);
        setRows([]);
        setLoading(false);
        setMsg("❌ Can't read users (rules? missing index? missing createdAt?)");
      }
    );

    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const t = safe(qText).toLowerCase();
    if (!t) return rows;
    return rows.filter((u) => {
      const a = safe(u.displayName).toLowerCase();
      const b = safe(u.email).toLowerCase();
      const c = safe(u.id).toLowerCase();
      return a.includes(t) || b.includes(t) || c.includes(t);
    });
  }, [rows, qText]);

  async function act(uid, patch) {
    setMsg("");
    try {
      await updateDoc(doc(db, "users", uid), {
        ...patch,
        updatedAt: serverTimestamp(),
      });
      setMsg("✅ Updated");
    } catch (e) {
      console.log(e);
      setMsg("❌ Failed (rules?)");
    }
  }

  async function banUser(u) {
    const reason = prompt("Ban reason (optional):") || "";
    await act(u.id, { isBanned: true, banReason: safe(reason) });
  }

  async function unbanUser(u) {
    await act(u.id, { isBanned: false, banReason: "" });
  }

  async function warnUser(u) {
    const note = prompt("Warning message (saved as lastWarning):") || "";
    await act(u.id, {
      lastWarning: safe(note),
      lastWarningAt: serverTimestamp(),
    });
  }

  async function resetFlags(u) {
    const ok = window.confirm("Reset flagsCount to 0?");
    if (!ok) return;
    await act(u.id, { flagsCount: 0 });
  }

  async function setRole(u) {
    if (!isAdmin) return;
    const next =
      prompt('Set role: "USER" | "SELLER" | "ADMIN"', safe(u.role || "USER")) ||
      "";
    const r = safe(next).toUpperCase();
    if (!["USER", "SELLER", "ADMIN"].includes(r)) {
      setMsg("❌ Invalid role");
      return;
    }
    await act(u.id, { role: r });
  }

  if (!isAdmin) {
    return (
      <div style={S.page}>
        <div style={S.top}>
          <button style={S.back} onClick={goBack}>
            ←
          </button>
          <div>
            <div style={S.h1}>Users</div>
            <div style={S.sub}>Admins only</div>
          </div>
        </div>
        <div style={S.card}>
          Only <b>ADMIN</b> can open Users & Roles.
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <div style={S.top}>
        <button style={S.back} onClick={goBack}>
          ←
        </button>
        <div>
          <div style={S.h1}>Users & Roles</div>
          <div style={S.sub}>Search, ban/unban, warn, reset flags, set roles.</div>
        </div>
        <div style={{ marginLeft: "auto" }}>
          <span style={S.badge}>{roleNorm}</span>
        </div>
      </div>

      <div style={S.wrap}>
        <div style={S.row}>
          <input
            style={S.input}
            value={qText}
            onChange={(e) => setQText(e.target.value)}
            placeholder="Search name / email / uid..."
          />
          <div style={S.kpi}>{filtered.length} users</div>
        </div>

        {msg ? <div style={S.msg}>{msg}</div> : null}

        <div style={S.grid}>
          <div style={S.list}>
            {loading ? (
              <div style={S.card}>Loading...</div>
            ) : filtered.length === 0 ? (
              <div style={S.card}>No users in /users.</div>
            ) : (
              filtered.map((u) => {
                const banned = !!u.isBanned;
                const r = safe(u.role || "USER").toUpperCase();

                return (
                  <button
                    key={u.id}
                    type="button"
                    style={{
                      ...S.userRow,
                      ...(selected?.id === u.id ? S.userRowOn : null),
                    }}
                    onClick={() => setSelected(u)}
                    title={u.id}
                  >
                    <div style={S.userName}>
                      {u.displayName || "No name"}
                      {banned ? <span style={S.pillBad}>BANNED</span> : null}
                      {r !== "USER" ? <span style={S.pill}>{r}</span> : null}
                    </div>
                    <div style={S.userSub}>{u.email || u.id}</div>
                  </button>
                );
              })
            )}
          </div>

          <div style={S.panel}>
            {!selected ? (
              <div style={S.card}>Pick a user to manage.</div>
            ) : (
              <div style={S.panelInner}>
                <div style={S.panelTitle}>User profile</div>

                <div style={S.pLine}>
                  UID: <span style={S.code}>{selected.id}</span>
                </div>
                <div style={S.pLine}>
                  Email: <b>{selected.email || "—"}</b>
                </div>
                <div style={S.pLine}>
                  Name: <b>{selected.displayName || "—"}</b>
                </div>

                <div style={S.pLine}>
                  Role: <b>{safe(selected.role || "USER").toUpperCase()}</b>{" "}
                  <button style={S.smallBtn} onClick={() => setRole(selected)}>
                    Change role
                  </button>
                </div>

                <div style={S.pLine}>
                  Flags: <b>{Number(selected.flagsCount || 0)}</b>{" "}
                  <button style={S.smallBtn} onClick={() => resetFlags(selected)}>
                    Reset flags
                  </button>
                </div>

                <div style={S.pLine}>
                  Banned: <b>{selected.isBanned ? "YES" : "NO"}</b>
                </div>

                <div style={S.pLine}>
                  Ban reason: <b>{selected.banReason || "—"}</b>
                </div>

                <div style={S.pLine}>
                  Last active: <b>{toTime(selected.lastActiveAt) || "—"}</b>
                </div>

                <div style={S.actions}>
                  {selected.isBanned ? (
                    <button style={S.btnGood} onClick={() => unbanUser(selected)}>
                      Unban
                    </button>
                  ) : (
                    <button style={S.btnBad} onClick={() => banUser(selected)}>
                      Ban
                    </button>
                  )}
                  <button style={S.btnWarn} onClick={() => warnUser(selected)}>
                    Warn
                  </button>
                </div>

                <div style={S.note}>
                  ⚠️ If your <code>/users</code> collection only has 1 doc, that’s
                  all you’ll see here. Make sure you create <code>users/{`{auth.uid}`}</code>{" "}
                  on signup/login.
                </div>
              </div>
            )}
          </div>
        </div>

        <div style={S.note2}>
          Index tip: you’re using <code>orderBy(createdAt)</code> so make sure every
          user doc has <code>createdAt</code>.
        </div>
      </div>
    </div>
  );
}

const S = {
  page: {
    minHeight: "100vh",
    padding: 14,
    background: "linear-gradient(180deg,#f7f8ff 0%,#eef2ff 45%,#f8fafc 100%)",
  },
  top: {
    maxWidth: 1100,
    margin: "0 auto 14px",
    display: "flex",
    gap: 12,
    alignItems: "center",
  },
  back: {
    width: 42,
    height: 42,
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "white",
    cursor: "pointer",
    fontWeight: 900,
  },
  h1: { fontSize: 18, fontWeight: 950, color: "#0f172a" },
  sub: { marginTop: 2, fontSize: 12, fontWeight: 800, color: "rgba(15,23,42,0.60)" },
  badge: {
    display: "inline-block",
    padding: "6px 10px",
    borderRadius: 999,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(255,255,255,0.9)",
    fontSize: 12,
    fontWeight: 900,
    color: "#0f172a",
  },
  wrap: { maxWidth: 1100, margin: "0 auto" },
  row: { display: "flex", gap: 10, alignItems: "center", marginBottom: 12 },
  input: {
    flex: 1,
    padding: "10px 12px",
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,0.12)",
    outline: "none",
    fontWeight: 800,
    background: "rgba(255,255,255,0.92)",
  },
  kpi: {
    padding: "10px 12px",
    borderRadius: 999,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(255,255,255,0.85)",
    fontSize: 12,
    fontWeight: 950,
  },
  msg: {
    marginBottom: 12,
    padding: 10,
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,0.08)",
    background: "rgba(255,255,255,0.75)",
    fontSize: 12,
    fontWeight: 900,
  },
  grid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
  list: { display: "grid", gap: 10 },
  userRow: {
    textAlign: "left",
    padding: 12,
    borderRadius: 16,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(255,255,255,0.92)",
    cursor: "pointer",
    boxShadow: "0 14px 35px rgba(15, 23, 42, 0.04)",
  },
  userRowOn: {
    border: "1px solid rgba(99,102,241,0.25)",
    background: "rgba(99,102,241,0.08)",
  },
  userName: { display: "flex", gap: 8, alignItems: "center", fontWeight: 950, color: "#0f172a" },
  userSub: { marginTop: 4, fontSize: 12, fontWeight: 800, color: "rgba(15,23,42,0.65)" },
  pill: {
    fontSize: 11,
    fontWeight: 950,
    padding: "4px 8px",
    borderRadius: 999,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(248,250,252,0.8)",
  },
  pillBad: {
    fontSize: 11,
    fontWeight: 950,
    padding: "4px 8px",
    borderRadius: 999,
    border: "1px solid rgba(239,68,68,0.22)",
    background: "rgba(239,68,68,0.10)",
  },
  panel: {
    borderRadius: 18,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(255,255,255,0.92)",
    boxShadow: "0 14px 35px rgba(15, 23, 42, 0.06)",
    padding: 14,
  },
  panelInner: {},
  panelTitle: { fontSize: 14, fontWeight: 950, color: "#0f172a", marginBottom: 10 },
  pLine: { fontSize: 12, fontWeight: 850, color: "rgba(15,23,42,0.72)", marginTop: 8 },
  code: { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" },
  actions: { display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" },
  smallBtn: {
    marginLeft: 8,
    padding: "6px 10px",
    borderRadius: 999,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "white",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 12,
  },
  btnGood: {
    padding: "10px 12px",
    borderRadius: 14,
    border: "1px solid rgba(34,197,94,0.20)",
    background: "rgba(34,197,94,0.12)",
    cursor: "pointer",
    fontWeight: 950,
  },
  btnWarn: {
    padding: "10px 12px",
    borderRadius: 14,
    border: "1px solid rgba(245,158,11,0.22)",
    background: "rgba(245,158,11,0.12)",
    cursor: "pointer",
    fontWeight: 950,
  },
  btnBad: {
    padding: "10px 12px",
    borderRadius: 14,
    border: "1px solid rgba(239,68,68,0.20)",
    background: "rgba(239,68,68,0.10)",
    cursor: "pointer",
    fontWeight: 950,
  },
  card: {
    padding: 14,
    borderRadius: 18,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(255,255,255,0.92)",
  },
  note: {
    marginTop: 12,
    padding: 10,
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,0.08)",
    background: "rgba(248,250,252,0.75)",
    fontSize: 12,
    fontWeight: 800,
    color: "rgba(15,23,42,0.72)",
  },
  note2: {
    marginTop: 12,
    padding: 10,
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,0.08)",
    background: "rgba(255,255,255,0.75)",
    fontSize: 12,
    fontWeight: 800,
    color: "rgba(15,23,42,0.72)",
  },
};
